import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Clock, 
  Calendar,
  ChevronRight,
  User,
  Truck,
  Shield,
  CreditCard,
  Eye,
  ExternalLink
} from 'lucide-react';
import { format, differenceInDays, parseISO, isWithinInterval, addDays } from 'date-fns';

const ExpiringDocumentsWidget = ({ 
  documents = [], 
  className = "",
  onDocumentClick,
  showActions = true,
  maxItems = 10,
  useSampleData = true
}) => {
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Sample data if none provided
  const defaultDocuments = [
    {
      id: '1',
      title: 'Commercial Driver License',
      type: 'license',
      entityName: 'John Smith',
      entityType: 'driver',
      expirationDate: '2025-01-15',
      issueDate: '2020-01-15',
      documentNumber: 'CDL-123456789',
      issuingAuthority: 'State DMV'
    },
    {
      id: '2',
      title: 'Vehicle Registration',
      type: 'registration',
      entityName: 'Truck TR-001',
      entityType: 'vehicle',
      expirationDate: '2025-01-08',
      issueDate: '2024-01-08',
      documentNumber: 'REG-987654321',
      issuingAuthority: 'State Motor Vehicle Division'
    },
    {
      id: '3',
      title: 'DOT Medical Certificate',
      type: 'medical',
      entityName: 'Sarah Johnson',
      entityType: 'driver',
      expirationDate: '2025-01-22',
      issueDate: '2023-01-22',
      documentNumber: 'MED-456789123',
      issuingAuthority: 'DOT Medical Examiner'
    },
    {
      id: '4',
      title: 'Insurance Policy',
      type: 'insurance',
      entityName: 'Fleet Coverage',
      entityType: 'company',
      expirationDate: '2025-02-01',
      issueDate: '2024-02-01',
      documentNumber: 'INS-789123456',
      issuingAuthority: 'TruckGuard Insurance'
    },
    {
      id: '5',
      title: 'Hazmat Endorsement',
      type: 'endorsement',
      entityName: 'Mike Wilson',
      entityType: 'driver',
      expirationDate: '2025-01-05',
      issueDate: '2020-01-05',
      documentNumber: 'HAZ-321654987',
      issuingAuthority: 'TSA'
    },
    {
      id: '6',
      title: 'Vehicle Inspection',
      type: 'inspection',
      entityName: 'Truck TR-002',
      entityType: 'vehicle',
      expirationDate: '2025-01-30',
      issueDate: '2024-01-30',
      documentNumber: 'INS-147258369',
      issuingAuthority: 'State Inspection Station'
    }
  ];

  const hasProvidedDocuments = Array.isArray(documents);
  const hasDocumentData = hasProvidedDocuments && documents.length > 0;
  const documentData = hasDocumentData
    ? documents
    : useSampleData
      ? defaultDocuments
      : [];

  // Calculate days until expiration and urgency level
  const processedDocuments = useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = addDays(now, 30);

    return documentData
      .map(doc => {
        const expirationDate = parseISO(doc.expirationDate);
        const daysUntilExpiration = differenceInDays(expirationDate, now);
        
        let urgency = 'low';
        let urgencyColor = 'success';
        let urgencyLabel = 'Good';

        if (daysUntilExpiration < 0) {
          urgency = 'expired';
          urgencyColor = 'danger';
          urgencyLabel = 'Expired';
        } else if (daysUntilExpiration <= 7) {
          urgency = 'critical';
          urgencyColor = 'danger';
          urgencyLabel = 'Critical';
        } else if (daysUntilExpiration <= 14) {
          urgency = 'high';
          urgencyColor = 'warning';
          urgencyLabel = 'High';
        } else if (daysUntilExpiration <= 30) {
          urgency = 'medium';
          urgencyColor = 'primary';
          urgencyLabel = 'Medium';
        }

        return {
          ...doc,
          expirationDate,
          daysUntilExpiration,
          urgency,
          urgencyColor,
          urgencyLabel,
          isExpiringSoon: isWithinInterval(expirationDate, { start: now, end: thirtyDaysFromNow })
        };
      })
      .filter(doc => doc.isExpiringSoon || doc.urgency === 'expired')
      .sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration);
  }, [documentData]);

  // Filter documents based on urgency
  const filteredDocuments = useMemo(() => {
    if (filterUrgency === 'all') return processedDocuments;
    return processedDocuments.filter(doc => doc.urgency === filterUrgency);
  }, [processedDocuments, filterUrgency]);

  const displayedDocuments = filteredDocuments.slice(0, maxItems);

  // Get document type icon
  const getDocumentIcon = (type) => {
    const iconMap = {
      license: User,
      registration: Truck,
      medical: Shield,
      insurance: CreditCard,
      endorsement: Shield,
      inspection: FileText
    };
    return iconMap[type] || FileText;
  };

  // Get entity type icon
  const getEntityIcon = (entityType) => {
    const iconMap = {
      driver: User,
      vehicle: Truck,
      company: Shield
    };
    return iconMap[entityType] || FileText;
  };

  const handleDocumentClick = (document) => {
    if (onDocumentClick) {
      onDocumentClick(document);
    } else {
      // Default action - toggle expanded view
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(document.id)) {
          newSet.delete(document.id);
        } else {
          newSet.add(document.id);
        }
        return newSet;
      });
    }
  };

  const urgencyOptions = [
    { value: 'all', label: 'All Documents', count: processedDocuments.length },
    { value: 'expired', label: 'Expired', count: processedDocuments.filter(d => d.urgency === 'expired').length },
    { value: 'critical', label: 'Critical', count: processedDocuments.filter(d => d.urgency === 'critical').length },
    { value: 'high', label: 'High', count: processedDocuments.filter(d => d.urgency === 'high').length },
    { value: 'medium', label: 'Medium', count: processedDocuments.filter(d => d.urgency === 'medium').length }
  ];

  return (
    <div className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning-100 rounded-lg">
              <Clock className="h-5 w-5 text-warning-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Expiring Documents</h3>
              <p className="text-sm text-slate-600">Next 30 days</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-slate-900">{processedDocuments.length}</span>
            <span className="text-sm text-slate-600">items</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center gap-1 bg-slate-100 rounded-lg p-1">
          {urgencyOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setFilterUrgency(option.value)}
              className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                filterUrgency === option.value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="hidden sm:inline">{option.label}</span>
              <span className="sm:hidden">{option.label.split(' ')[0]}</span>
              {option.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  filterUrgency === option.value
                    ? 'bg-slate-100 text-slate-700'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {option.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Document List */}
      <div className="max-h-96 overflow-y-auto">
        {displayedDocuments.length === 0 ? (
          <div className="p-6 text-center">
            <Clock className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">No documents expiring in the selected timeframe</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {displayedDocuments.map((document) => {
              const DocumentIcon = getDocumentIcon(document.type);
              const EntityIcon = getEntityIcon(document.entityType);
              const isExpanded = expandedItems.has(document.id);
              
              return (
                <div key={document.id} className="hover:bg-slate-50 transition-colors duration-200">
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => handleDocumentClick(document)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`p-2 rounded-lg bg-${document.urgencyColor}-100`}>
                          <DocumentIcon className={`h-4 w-4 text-${document.urgencyColor}-600`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-slate-900 truncate">
                              {document.title}
                            </h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${document.urgencyColor}-100 text-${document.urgencyColor}-700`}>
                              {document.urgencyLabel}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-sm text-slate-600 mb-1">
                            <EntityIcon className="h-3 w-3" />
                            <span>{document.entityName}</span>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs text-slate-500 space-y-1 sm:space-y-0">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {document.daysUntilExpiration < 0 
                                  ? `Expired ${Math.abs(document.daysUntilExpiration)} days ago`
                                  : document.daysUntilExpiration === 0
                                  ? 'Expires today'
                                  : `${document.daysUntilExpiration} days remaining`
                                }
                              </span>
                            </div>
                            <span className="hidden sm:inline">•</span>
                            <span>{format(document.expirationDate, 'MMM dd, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1 sm:space-x-2 ml-2 sm:ml-3">
                        {showActions && (
                          <>
                            <button 
                              className="p-1 sm:p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle view action
                              }}
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            </button>
                            <button 
                              className="p-1 sm:p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle external link action
                              }}
                            >
                              <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                            </button>
                          </>
                        )}
                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                          isExpanded ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-slate-50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs">
                        <div>
                          <span className="font-medium text-slate-700">Document Number:</span>
                          <p className="text-slate-600 break-all">{document.documentNumber || '—'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Issuing Authority:</span>
                          <p className="text-slate-600">{document.issuingAuthority || '—'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Issue Date:</span>
                          <p className="text-slate-600">
                            {document.issueDate
                              ? format(parseISO(document.issueDate), 'MMM dd, yyyy')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Entity Type:</span>
                          <p className="text-slate-600 capitalize">{document.entityType}</p>
                        </div>
                      </div>
                      
                      {showActions && (
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-3 pt-3 border-t border-slate-200">
                          <button className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded-md hover:bg-primary-700 transition-colors duration-200">
                            Renew Document
                          </button>
                          <button className="px-3 py-1.5 bg-slate-200 text-slate-700 text-xs rounded-md hover:bg-slate-300 transition-colors duration-200">
                            Set Reminder
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredDocuments.length > maxItems && (
        <div className="p-4 border-t border-slate-200 text-center">
          <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            View all {filteredDocuments.length} documents
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpiringDocumentsWidget;
